import React, { ChangeEvent, FC, useEffect, useState } from 'react';

// material-ui
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';

// project imports
import QuotesList from './quotes';
import MainCard from 'ui-component/cards/MainCard';

// assets
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useAgencyStore, useRegionStore } from 'Application/store';
import { concatenateDestinations, exportToCSV, getAgencyIdFromStorage, getUserRoleNameFromStorage } from 'Application/utils';
import { Flag, QuoteStatus, UserRole } from 'Application/models';
import { Box, Divider, Drawer, FormControl, IconButton, InputLabel, Select, SelectChangeEvent, TextField } from '@mui/material';
import { Close, DownloadRounded, FilterList, Search } from '@mui/icons-material';
import Loader from 'ui-component/Loader';
import { toast } from 'sonner';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';

// ==============================|| USER LIST STYLE 1 ||============================== //

const getEnumFromString = (value: string): QuoteStatus | undefined => {
    return QuoteStatus[value as keyof typeof QuoteStatus];
};

interface GeneratedQuotesProps {
    agencyId: number;
    allowAllFlag?: boolean;
}

type QuotesFilters = {
    quoteStatus: string;
    agencyName: string;
    regionName: string;
    startDate: string | null;
    endDate: string | null;
};

//! Failed & Pending Quotations component
export const GeneratedQuotes: FC<GeneratedQuotesProps> = ({ agencyId, allowAllFlag = false }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

    const userRole = getUserRoleNameFromStorage();

    const { regions, isLoading: isRegionsLoading } = useRegionStore();
    const countryValues = regions && [
        { name: 'All', regionID: 0 },
        ...regions!.map((region) => {
            return {
                name: region.name,
                regionID: region.regionID
            };
        })
    ];

    // Default to SELF
    const defaultStatus = allowAllFlag ? 'ALL' : 'SELF';

    const initialQuoteFilters: QuotesFilters = {
        agencyName: '',
        quoteStatus: defaultStatus,
        regionName: countryValues != null ? countryValues[0].name : 'All',
        startDate: null,
        endDate: null
    };

    const [tempQuoteFilters, setTempQuoteFilters] = useState<QuotesFilters>(initialQuoteFilters);
    const [quoteFilters, setQuoteFilters] = useState<QuotesFilters>(initialQuoteFilters);

    const handleTempQuoteFiltersChange = (key: keyof QuotesFilters, value: string) => {
        setTempQuoteFilters((prevTempFilters) => {
            return {
                ...prevTempFilters,
                [key]: value
            };
        });
    };

    const handleQuoteFiltersChange = () => {
        setQuoteFilters(tempQuoteFilters);
        setPage(1);
        setIsDrawerOpen(false);
    };

    const removeQuoteFilters = () => {
        setQuoteFilters(initialQuoteFilters);
        setTempQuoteFilters(initialQuoteFilters);
        setPage(1);
    };

    const quotationStatusOptions = allowAllFlag
        ? ['ALL', 'SELF', ...Object.values(QuoteStatus)] // Include ALL when allowed
        : ['SELF', ...Object.values(QuoteStatus)]; // Otherwise, only SELF

    const [quotationStatus, setQuotationStatus] = useState<string>(defaultStatus);
    // Default to SELF

    const [tempQuotationNumber, setTempQuotationNumber] = useState<string>('');
    const [quotationNumber, setQuotationNumber] = useState<string>('');

    const handleQuotationNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setTempQuotationNumber(value);
    };

    const {
        fetchBritamInternalUsersQuotations,
        agencyQuotationsData,
        isLoading: isGetQuotationsLoading,
        isError: isGetQuotationsError,
        error: getQuotationsError
    } = useAgencyStore((state) => ({
        fetchBritamInternalUsersQuotations: state.fetchBritamInternalUsersQuotations,
        agencyQuotationsData: state.agencyQuotationsData,
        isLoading: state.isLoading,
        isError: state.isError,
        error: state.error
    }));

    const {
        quotationsReportData,
        getQuotationsReportData,
        isLoading: isGetQuotationsReportLoading
    } = useAgencyStore((state) => ({
        quotationsReportData: state.quotationsReportData,
        getQuotationsReportData: state.getQuotationsReportData,
        isLoading: state.isLoading
    }));

    const { isLoading: isGetAgencyNameLoading, agencyNameData } = useAgencyStore((state) => ({
        getAgenciesName: state.getAgenciesName,
        isLoading: state.isLoading,
        agencyNameData: state.agencyNameData
    }));

    const quotationStatusOption = getEnumFromString(quoteFilters.quoteStatus);

    useEffect(() => {
        const quotationReportRegion = userRole === UserRole.ROLE_ADMINISTRATOR ? quoteFilters.regionName : undefined;

        fetchBritamInternalUsersQuotations(
            page,
            pageSize,
            allowAllFlag ? Flag.All : Flag.SELF, // Always use SELF in this case
            quotationStatusOption,
            quotationNumber,
            quoteFilters.agencyName,
            quoteFilters.regionName,
            quoteFilters.startDate ?? undefined,
            quoteFilters.endDate ?? undefined
        );
        getQuotationsReportData(
            quotationNumber,
            quotationStatusOption,
            quoteFilters.agencyName,
            quotationReportRegion,
            quoteFilters.startDate ?? undefined,
            quoteFilters.endDate ?? undefined
        );
    }, [
        page,
        pageSize,
        allowAllFlag,
        quotationNumber,
        quoteFilters.quoteStatus,
        quoteFilters.agencyName,
        quoteFilters.regionName,
        quoteFilters.startDate,
        quoteFilters.endDate
    ]);

    useEffect(() => {
        // If the search bar is cleared, fetch all quotations
        if (tempQuotationNumber == '') {
            handleQuotationNumberSearch();
        }
    }, [tempQuotationNumber]);

    const handleQuotationNumberSearch = async () => {
        setQuotationNumber(tempQuotationNumber);
        setPage(1);
    };

    const handleRowsPerPageChange = (rows: number) => {
        setPageSize(rows);
        setPage(1); // Reset to first page when rows per page change
        handleClose();
    };

    const handleQuotesDataExport = async () => {
        if (!quotationsReportData || (quotationsReportData && quotationsReportData.length === 0)) {
            toast.warning('No data available to export.');
            return;
        }

        const exportContent = quotationsReportData.map((quotation) => {
            const { destinations } = quotation;
            const destinationList = destinations.length === 1 ? destinations[0].destinationName : concatenateDestinations(destinations);

            return {
                'Quotation number': quotation.quotationNumber,
                'Quotation status': quotation.quotationStatus,
                'Customer name': quotation.customerName,
                'Customer email address': quotation.customerEmailAddress,
                'Customer phone number': quotation.customerPhoneNumber,
                Plan: quotation.planName,
                'Travelling from': quotation.sourceLocation,
                'Travelling to': destinationList,
                'Departure date': quotation.departureDate,
                'Return date': quotation.returnDate,
                'Total premium': quotation.totalPremium,
                Agent: quotation.agent,
                Intermediary: quotation.intermediary
            };
        });

        exportToCSV('Quotations', exportContent);
    };

    return (
        <>
            <Box className="flex flex-wrap items-center justify-left gap-x-4 mb-4">
                <Box className="col-span-full sm:w-1/3.5">
                    <TextField
                        size="small"
                        id="quoteNumber"
                        label="Search by quote number"
                        variant="outlined"
                        value={tempQuotationNumber}
                        onChange={handleQuotationNumberChange}
                        onKeyDown={(e) => e.key === 'Enter' && handleQuotationNumberSearch()}
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Search className="hover:cursor-pointer" onClick={handleQuotationNumberSearch} />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
                <Box className="flex flex-wrap gap-x-4 mt-4 sm:mt-0">
                    {/* Button to Open Drawer */}
                    <Button variant="contained" startIcon={<FilterList />} onClick={() => setIsDrawerOpen(true)}>
                        Filter
                    </Button>

                    {!isGetQuotationsLoading && !isGetQuotationsReportLoading && (
                        <Button endIcon={<DownloadRounded />} disableElevation variant="contained" onClick={handleQuotesDataExport}>
                            Export Data
                        </Button>
                    )}
                </Box>
            </Box>

            <Drawer anchor="right" open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
                <Box width={350} p={3} role="presentation">
                    {/* Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <h2>Filters</h2>
                        <IconButton onClick={() => setIsDrawerOpen(false)}>
                            <Close />
                        </IconButton>
                    </Box>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid container spacing={2} mt={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="quote-status-label">Quote Status</InputLabel>
                                    <Select
                                        size="small"
                                        labelId="quote-status-label"
                                        id="quote-status"
                                        value={tempQuoteFilters.quoteStatus}
                                        fullWidth
                                        label="Quote Status"
                                        variant="outlined"
                                        onChange={(e: SelectChangeEvent) => handleTempQuoteFiltersChange('quoteStatus', e.target.value)}
                                    >
                                        {quotationStatusOptions.map((quoteStatusOption) => (
                                            <MenuItem key={quoteStatusOption} value={quoteStatusOption}>
                                                {quoteStatusOption.toTitle()}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="agency-name-label">Intermediary Name</InputLabel>
                                    <Select
                                        labelId="agency-name-label"
                                        value={tempQuoteFilters.agencyName} // Bind to tempSelectedAgency
                                        onChange={(e) => handleTempQuoteFiltersChange('agencyName', e.target.value)}
                                        label="Agency Name"
                                    >
                                        {isGetAgencyNameLoading ? (
                                            <MenuItem disabled>Loading...</MenuItem>
                                        ) : (
                                            agencyNameData?.map((agency: { name: string }) => (
                                                <MenuItem key={agency.name} value={agency.name}>
                                                    {agency.name}
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {/* Date Pickers */}
                            <Grid item xs={6}>
                                <DatePicker
                                    label="Start Date"
                                    value={tempQuoteFilters.startDate}
                                    onChange={(newValue) => newValue && handleTempQuoteFiltersChange('startDate', newValue)}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <DatePicker
                                    label="End Date"
                                    value={tempQuoteFilters.endDate}
                                    onChange={(newValue) => newValue && handleTempQuoteFiltersChange('endDate', newValue)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                {userRole === UserRole.ROLE_ADMINISTRATOR && (
                                    <FormControl fullWidth>
                                        <InputLabel id="search-country-label">Search by country</InputLabel>
                                        <Select
                                            labelId="search-country-label"
                                            id="search-country"
                                            value={tempQuoteFilters.regionName}
                                            fullWidth
                                            label="Search by country"
                                            variant="outlined"
                                            onChange={(e: SelectChangeEvent) => handleTempQuoteFiltersChange('regionName', e.target.value)}
                                        >
                                            {isRegionsLoading ? (
                                                <MenuItem disabled>Loading...</MenuItem>
                                            ) : (
                                                countryValues?.map((country) => {
                                                    return (
                                                        <MenuItem key={country.regionID} value={country.name}>
                                                            {country.name}
                                                        </MenuItem>
                                                    );
                                                })
                                            )}
                                        </Select>
                                    </FormControl>
                                )}
                            </Grid>
                        </Grid>
                    </LocalizationProvider>

                    {/* Buttons */}
                    <Box mt={3} display="flex" justifyContent="space-between">
                        <Button variant="outlined" color="secondary" onClick={removeQuoteFilters}>
                            Clear Filters
                        </Button>

                        <Button variant="contained" onClick={handleQuoteFiltersChange}>
                            Apply Filters
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <Divider className="my-6" />

            <Box>
                {isGetQuotationsLoading || agencyQuotationsData === null ? (
                    <Loader />
                ) : isGetQuotationsError ? (
                    <Typography color="error">{getQuotationsError}</Typography>
                ) : (
                    <>
                        <QuotesList agencyQuotations={agencyQuotationsData} />
                        <Grid container justifyContent="space-between" alignItems="end">
                            {agencyQuotationsData.totalPages > 1 && (
                                <Grid item>
                                    <Pagination
                                        className="mt-6"
                                        count={agencyQuotationsData.totalPages}
                                        page={page}
                                        onChange={(event, value) => setPage(value)}
                                        color="primary"
                                    />
                                </Grid>
                            )}
                            <Grid item>
                                <Button
                                    size="large"
                                    sx={{ color: 'grey.900' }}
                                    color="secondary"
                                    endIcon={<ExpandMoreRoundedIcon />}
                                    onClick={handleClick}
                                >
                                    {pageSize} Rows
                                </Button>
                                {anchorEl && (
                                    <Menu
                                        id="menu-user-list-style1"
                                        anchorEl={anchorEl}
                                        keepMounted
                                        open={Boolean(anchorEl)}
                                        onClose={handleClose}
                                        variant="selectedMenu"
                                        anchorOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right'
                                        }}
                                        transformOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right'
                                        }}
                                    >
                                        {[10, 20, 30].map((rows) => (
                                            <MenuItem key={rows} onClick={() => handleRowsPerPageChange(rows)}>
                                                {rows} Rows
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                )}
                            </Grid>
                        </Grid>
                    </>
                )}
            </Box>
        </>
    );
};

export const AgencyUserGeneratedQuotes = () => {
    const agencyId: number = getAgencyIdFromStorage()!;

    return (
        <MainCard title={<Typography variant="h3">My Quotes</Typography>}>
            <GeneratedQuotes agencyId={agencyId} allowAllFlag={false} />
        </MainCard>
    );
};
